import { z } from 'zod';
// ============================================================
// Enums
// ============================================================
export const FamilyDisplayModeSchema = z.enum([
    'full_names',
    'single_parent_father',
    'single_parent_mother',
    'self_only',
]);
export const TemplateCategorySchema = z.enum([
    'CLASSIC',
    'MODERN',
    'VINTAGE',
    'FLORAL',
    'MINIMAL',
    'ELEGANT',
    'NATURAL',
]);
export const InvitationStatusSchema = z.enum(['PUBLISHED', 'EXPIRED', 'DELETED']);
export const InvitationTypeSchema = z.enum(['COUPLE', 'GROOM_PARENT', 'BRIDE_PARENT']);
// ============================================================
// Section Ordering
// ============================================================
export const REORDERABLE_SECTIONS = [
    'greeting',
    'parents',
    'ceremony',
    'venue',
    'directions',
    'gallery',
    'contacts',
    'accounts',
    'rsvp',
    'guestbook',
    'timeline',
    'schedule',
    'notices',
    'wreath',
    'video',
];
export const DEFAULT_SECTION_ORDER = [...REORDERABLE_SECTIONS];
export const SECTION_LABELS = {
    greeting: '인사말',
    parents: '기본 정보',
    ceremony: '예식 일정',
    venue: '예식 장소',
    directions: '교통편',
    gallery: '갤러리',
    contacts: '연락처',
    accounts: '계좌번호',
    rsvp: '참석 여부',
    guestbook: '방명록',
    timeline: '타임라인',
    schedule: '식순',
    notices: '안내사항',
    wreath: '화환',
    video: '영상',
};
export function sanitizeSectionOrder(order) {
    if (!order)
        return [...DEFAULT_SECTION_ORDER];
    const seen = new Set();
    const valid = order.filter((id) => {
        if (seen.has(id) || !REORDERABLE_SECTIONS.includes(id))
            return false;
        seen.add(id);
        return true;
    });
    // 저장값에 빠진 섹션은 맨 뒤가 아니라 DEFAULT_SECTION_ORDER상의 "앞 이웃" 뒤에 끼워 넣는다.
    // 예: directions가 신규 추가되어 기존 저장값(directions 없음)에서 빠졌을 때, 맨 뒤로 밀지 않고
    //     기본 순서에서 directions 앞에 오는 venue 뒤에 삽입 → 기존 청첩장 레이아웃이 유지된다.
    const result = [...valid];
    const missing = DEFAULT_SECTION_ORDER.filter((id) => !seen.has(id));
    for (const id of missing) {
        const defaultIdx = DEFAULT_SECTION_ORDER.indexOf(id);
        // 기본 순서에서 이 섹션 바로 앞에 오면서 result에 이미 존재하는 가장 가까운 앵커를 찾는다.
        let insertAt = result.length;
        for (let i = defaultIdx - 1; i >= 0; i--) {
            const anchor = DEFAULT_SECTION_ORDER[i];
            const anchorIdx = result.indexOf(anchor);
            if (anchorIdx !== -1) {
                insertAt = anchorIdx + 1;
                break;
            }
        }
        result.splice(insertAt, 0, id);
        seen.add(id);
    }
    return result;
}
// ============================================================
// 타임라인 / 일정 / 안내사항
// ============================================================
export const TimelineItemSchema = z.object({
    date: z.string().optional(),
    title: z.string(),
    description: z.string().optional(),
    imageUrl: z.string().optional(),
});
export const TimelineSchema = z.array(TimelineItemSchema).default([]);
export const ScheduleItemSchema = z.object({
    time: z.string(),
    title: z.string(),
    description: z.string().optional(),
});
export const ScheduleSchema = z.array(ScheduleItemSchema).default([]);
export const NoticeItemSchema = z.object({
    icon: z.string().optional(),
    title: z.string(),
    description: z.string().optional(),
});
export const NoticesSchema = z.array(NoticeItemSchema).default([]);
// ============================================================
// Cover (커버/히어로)
// ============================================================
export const CoverHeightSchema = z.enum(['sm', 'md', 'lg', 'xl', 'full']).default('md');
export const CoverTextPositionSchema = z.enum(['top', 'center', 'bottom']).default('bottom');
export const CoverNameStyleSchema = z.enum(['horizontal', 'vertical']).default('horizontal');
export const CoverSchema = z.object({
    image: z.string().nullable().optional(),
    overlayColor: z.string().default('#000000'),
    overlayOpacity: z.number().min(0).max(1).default(0.3),
    subtitle: z.string().default('결혼합니다'),
    textColor: z.string().optional(),
    nameStyle: CoverNameStyleSchema,
    // 자동 입력 항목 — 기본 ON. 에디터(?? true)·템플릿(!= false)과 일치해야 함:
    // default(false)면 저장 시 스키마 parse가 false를 실체화해 토글이 꺼져 보임
    showDate: z.boolean().default(true),
    showVenue: z.boolean().default(true),
    showAddress: z.boolean().default(true),
    infoText: z.string().optional(),
    height: CoverHeightSchema,
    textPosition: CoverTextPositionSchema,
});
export const LandingSchema = z.object({
    message: z.string().default('결혼합니다'),
    showDate: z.boolean().default(true),
    style: z.enum(['photo-blur', 'gradient', 'minimal']).default('photo-blur'),
});
// ============================================================
// Sub-schemas (strict — 발행 시 검증용)
// ============================================================
export const PersonSchema = z.object({
    name: z.string().min(1, '이름을 입력해주세요'),
    nameEn: z.string().optional(),
    fatherName: z.string().optional(),
    fatherNameEn: z.string().optional(),
    motherName: z.string().optional(),
    motherNameEn: z.string().optional(),
    isDeceased: z
        .object({
        father: z.boolean().optional(),
        mother: z.boolean().optional(),
    })
        .optional(),
    phone: z.string().optional(),
    relation: z.string().optional(),
    displayMode: FamilyDisplayModeSchema.optional(),
});
/**
 * 계좌 — 드래프트 허용. 일부만 입력해도 저장되고, 청첩장 노출 여부는
 * 렌더 매퍼(render/data-mapper.ts isRenderableAccount)가 셋 다 채워졌는지로 판정한다.
 * 과거에는 셋 다 min(1) 필수라 불완전 계좌가 저장 페이로드에서 조용히 빠졌고,
 * 필드를 비우는 편집이 서버에 반영되지 않는 문제가 있었다.
 */
export const AccountSchema = z.object({
    bank: z.string().default(''),
    accountNumber: z.string().default(''),
    accountHolder: z.string().default(''),
    tossPayLink: z.string().optional(),
    kakaoPayLink: z.string().optional(),
});
export const ParentAccountsSchema = z
    .object({
    father: z.array(AccountSchema).default([]),
    mother: z.array(AccountSchema).default([]),
})
    .optional();
export const VenueSchema = z.object({
    name: z.string().min(1, '예식장 이름을 입력해주세요'),
    hall: z.string().optional(),
    address: z.string().min(1, '주소를 입력해주세요'),
    lat: z.number().optional(),
    lng: z.number().optional(),
    tel: z.string().optional(),
    transportation: z.string().optional(),
    parking: z.string().optional(),
    parkingImages: z.array(z.string()).default([]),
});
export const GreetingFontSizeSchema = z.enum(['sm', 'md', 'lg', 'xl', 'custom']).default('md');
export const ContentSchema = z.object({
    greeting: z.string().optional(),
    notice: z.string().optional(),
    greetingFontSize: GreetingFontSizeSchema.optional(),
    greetingFontSizeCustom: z.number().optional(),
    // RSVP 섹션 본문 문구. 미설정 시 템플릿 기본 문구 사용.
    rsvpDesc: z.string().optional(),
    // 계좌 안내 섹션 본문 문구. 미설정 시 템플릿 기본 문구 사용.
    accountsDesc: z.string().optional(),
    // 화환 섹션 본문 문구. 미설정 시 템플릿 기본 문구 사용.
    wreathDesc: z.string().optional(),
});
export const GallerySchema = z.object({
    coverImage: z.string().optional(),
    images: z.array(z.string()).default([]),
    // 이미지별 초점(object-position) 문자열. images와 같은 index로 정렬된다.
    // 값 예: "50% 20%". 미설정(빈 문자열/누락)이면 렌더 시 center(기본값)로 처리.
    imagePositions: z.array(z.string()).optional(),
});
export const RsvpFieldsSchema = z.object({
    phone: z.boolean().default(true),
    side: z.boolean().default(false),
    attendance: z.boolean().default(true),
    meal: z.boolean().default(true),
    bus: z.boolean().default(false),
    name: z.boolean().default(true),
    companionName: z.boolean().default(false),
    guestCount: z.boolean().default(true),
    message: z.boolean().default(true),
    deadline: z.string().optional(),
});
export const SettingsSchema = z.object({
    showParents: z.boolean().default(true),
    showAccounts: z.boolean().default(true),
    // 계좌 아코디언을 청첩장 진입 시 펼친 상태로 표시 (기본 OFF = 접힘)
    accountsDefaultOpen: z.boolean().default(false),
    showMap: z.boolean().default(true),
    // 예식 일정 섹션의 D-day 카운트다운(일/시간/분/초) 표시 여부. 기본 ON.
    // 표시 토글이므로 렌더 시 === false 만 체크(미설정/true 는 표시 유지).
    // default(false) 금지 — parse 시 false 가 실체화돼 기존 청첩장이 꺼져 보임(위 :154 주석 참조).
    showCountdown: z.boolean().default(true),
    // 신랑/신부/부모 이름에 영문 표시 여부 (에디터 "영문 이름 입력" 토글)
    useEnglishNames: z.boolean().default(false),
    // 신랑/신부 본인 이름에서 성(姓)을 숨기고 이름만 표시 (렌더 시점 dropSurname 필터로 처리)
    hideSurname: z.boolean().default(false),
    enableRsvp: z.boolean().default(true),
    // 청첩장 최초 진입 시 RSVP 모달 자동 팝업 (세션 1회 + 제출 시 영구 억제). 기본 OFF.
    rsvpAutoPopup: z.boolean().default(false),
    rsvpFields: RsvpFieldsSchema.optional(),
    backgroundColor: z.string().optional(),
    textColor: z.string().optional(),
    // 포인트 컬러(버튼/RSVP 라디오·포커스/체크박스/타임라인 강조). 미설정 시 템플릿 기본색 유지.
    // CSS 주입 방지: #RGB / #RRGGBB hex 형식만 허용 (} breakout 차단).
    pointColor: z
        .string()
        .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
        .optional(),
    // CSS 주입 방지: 폰트 이름에 허용되는 문자만 (영문/한글/숫자/공백/콤마/따옴표/하이픈/언더스코어)
    fontFamily: z
        .string()
        .max(64)
        .regex(/^[A-Za-z0-9가-힣 ,'"\-_]+$/)
        .optional(),
    // path traversal 방지: slug 형식만 (소문자/숫자/하이픈)
    fontSlug: z
        .string()
        .max(64)
        .regex(/^[a-z0-9-]+$/)
        .optional(),
    textScale: z.enum(['sm', 'md', 'lg']).default('md'),
    sectionOrder: z.array(z.string()).optional(),
    calendarStyle: z.enum(['none', 'calendar', 'countdown', 'minimal']).default('calendar'),
    galleryLayout: z
        .enum(['grid-2', 'grid-3', 'grid-2-1', 'carousel', 'filmstrip', 'highlight'])
        .default('grid-3'),
    // 화면 확대 방지 — 핀치줌/브라우저 줌으로 화면 전체를 확대하는 것을 차단 (모바일·데스크탑). 라이트박스(탭→크게보기)는 유지.
    disablePhotoZoom: z.boolean().default(false),
    // 레거시: 서버는 이 값을 무시한다(저장 시 strip). 비밀번호 쓰기는 POST /api/invitations/:id/privacy 만 (FIKET-459)
    password: z.string().optional(),
    customSlug: z.string().optional(),
    sectionSpacing: z.record(z.string(), z.string()).optional(),
    globalSectionSpacing: z.enum(['default', 'lg', 'xl']).or(z.string()).optional(),
    fadeSpeed: z.enum(['slow', 'normal', 'fast', 'none']).optional(),
    // cuid2 형식 (24자 영소문자+숫자)
    loadingDesignId: z
        .string()
        .regex(/^[a-z0-9]{24}$/)
        .optional(),
});
// 섹션별 부제(subtitle) 사용자 지정값. 미설정 시 템플릿 default 문구 사용.
// 템플릿 변수 `sectionSubtitles`와 1:1 매핑.
export const SectionSubtitlesSchema = z.object({
    greeting: z.string().optional(),
    gallery: z.string().optional(),
    venue: z.string().optional(),
    directions: z.string().optional(),
    timeline: z.string().optional(),
    schedule: z.string().optional(),
    contacts: z.string().optional(),
    notices: z.string().optional(),
    rsvp: z.string().optional(),
});
// 사용자 지정 라벨. 미설정 시 템플릿 default 문구 사용.
// 템플릿 변수 `labels`와 1:1 매핑. 계좌 아코디언 제목 전용
// (연락처와 공유되는 groomSide/brideSide와 분리).
export const LabelsSchema = z.object({
    groomAccountTitle: z.string().optional(),
    brideAccountTitle: z.string().optional(),
    // 화환 섹션 버튼 라벨. 미설정 시 템플릿 기본 문구 사용.
    wreathButton: z.string().optional(),
});
export const EnabledSectionsSchema = z.object({
    greeting: z.boolean().optional(),
    gallery: z.boolean().optional(),
    accounts: z.boolean().optional(),
    rsvp: z.boolean().optional(),
    guestbook: z.boolean().optional(),
    ending: z.boolean().optional(),
    bgm: z.boolean().optional(),
    timeline: z.boolean().optional(),
    schedule: z.boolean().optional(),
    notices: z.boolean().optional(),
    venue: z.boolean().optional(),
    contacts: z.boolean().optional(),
    landing: z.boolean().optional(),
    video: z.boolean().optional(),
    directions: z.boolean().optional(),
    wreath: z.boolean().optional(),
});
/**
 * 화환 주문 URL — 빈 문자열 허용(미설정 시 전역 기본값으로 fallback),
 * 값이 있으면 http/https만 허용한다. 사용자 입력이 href에 그대로 들어가므로
 * `javascript:` 등 다른 프로토콜 주입을 막는다.
 */
export const WreathUrlSchema = z
    .string()
    .trim()
    .max(2048)
    .refine((v) => v === '' || /^https?:\/\//i.test(v), {
    message: 'http 또는 https로 시작하는 URL만 입력할 수 있습니다',
});
export const WreathSchema = z.object({
    orderUrl: WreathUrlSchema.optional(),
    // 화환 거절 모드 — true면 렌더 시 버튼/주문URL 숨기고 안내 문구만 노출
    declined: z.boolean().optional(),
});
export const EndingSchema = z.object({
    imageUrl: z.string().optional(),
    message: z.string().optional(),
});
export const ShareSchema = z.object({
    ogImageUrl: z.string().optional(),
    ogTitle: z.string().optional(),
    ogDescription: z.string().optional(),
});
export const BgmSchema = z.object({
    url: z.string(),
    title: z.string().optional(),
    autoplay: z.boolean().default(true),
    loop: z.boolean().default(true),
    volume: z.number().min(0).max(1).default(0.5),
    source: z.enum(['shared', 'user']).optional(),
    uploadedAt: z.string().optional(),
});
export const VideoSchema = z.object({
    url: z.string(),
    thumbnail: z.string().optional(),
    autoplay: z.boolean().default(false),
    title: z.string().optional(),
});
// ============================================================
// InvitationData — JSONB `data` 컬럼 스키마
// ============================================================
/** data JSONB 내부 Person (이름 필수 아님 — 드래프트 허용) */
const DataPersonSchema = z.object({
    name: z.string().default(''),
    nameEn: z.string().optional(),
    fatherName: z.string().optional(),
    fatherNameEn: z.string().optional(),
    motherName: z.string().optional(),
    motherNameEn: z.string().optional(),
    isDeceased: z
        .object({
        father: z.boolean().optional(),
        mother: z.boolean().optional(),
    })
        .optional(),
    phone: z.string().optional(),
    fatherPhone: z.string().optional(),
    motherPhone: z.string().optional(),
    relation: z.string().optional(),
    displayMode: FamilyDisplayModeSchema.optional(),
    account: AccountSchema.optional(),
    parentAccounts: ParentAccountsSchema,
    /** 계좌 노출 체크박스 상태 (명시적 저장값). 미설정(undefined)이면 데이터 존재 여부로 파생 — 하위호환 */
    accountToggles: z
        .object({
        personal: z.boolean().optional(),
        father: z.boolean().optional(),
        mother: z.boolean().optional(),
    })
        .optional(),
});
/** data JSONB 내부 Venue (주소 필수 아님 — 드래프트 허용) */
const DataVenueSchema = z.object({
    name: z.string().default(''),
    hall: z.string().optional(),
    address: z.string().default(''),
    lat: z.number().optional(),
    lng: z.number().optional(),
    tel: z.string().optional(),
    transportation: z.string().optional(),
    parking: z.string().optional(),
    parkingImages: z.array(z.string()).default([]),
});
const DEFAULT_PERSON = { name: '' };
const DEFAULT_VENUE = { name: '', address: '', parkingImages: [] };
export const InvitationDataSchema = z
    .object({
    groom: DataPersonSchema.default(DEFAULT_PERSON),
    bride: DataPersonSchema.default(DEFAULT_PERSON),
    wedding: z
        .object({
        date: z.string().optional(),
        venue: DataVenueSchema.default(DEFAULT_VENUE),
    })
        .default(() => ({ venue: DEFAULT_VENUE })),
    content: ContentSchema.default({}),
    gallery: GallerySchema.default(() => ({ images: [] })),
    settings: SettingsSchema.partial().default({}),
    enabledSections: EnabledSectionsSchema.optional(),
    ending: EndingSchema.optional(),
    share: ShareSchema.optional(),
    bgm: BgmSchema.optional(),
    video: VideoSchema.optional(),
    wreath: WreathSchema.optional(),
    timeline: TimelineSchema,
    schedule: ScheduleSchema,
    notices: NoticesSchema,
    cover: CoverSchema.optional(),
    landing: LandingSchema.optional(),
    sectionSubtitles: SectionSubtitlesSchema.optional(),
    labels: LabelsSchema.optional(),
    customTheme: z.any().optional(),
    /**
     * 카탈로그 변수의 청첩장별 사용자 입력값. 키는 카탈로그 path 를 **통째로** 쓴다
     * (`'sectionTitles.gallery'` 가 한 키 — 중첩 객체가 아니다).
     *
     * z.record 인 이유: InvitationDataSchema 는 평범한 z.object 라 선언되지 않은 키를
     * 조용히 버린다(labels.* 32개가 탈락하던 원인). 카탈로그는 런타임에 늘어나므로
     * 키를 미리 선언할 수 없다 — z.record 가 그 함정을 구조적으로 회피한다.
     */
    varOverrides: z.record(z.string(), z.unknown()).optional(),
})
    .default(() => ({
    groom: DEFAULT_PERSON,
    bride: DEFAULT_PERSON,
    wedding: { venue: DEFAULT_VENUE },
    content: {},
    gallery: { images: [] },
    settings: {},
    timeline: [],
    schedule: [],
    notices: [],
}));
// ============================================================
// Main Invitation Schema
// ============================================================
export const InvitationSchema = z.object({
    id: z.string(),
    userId: z.string(),
    templateId: z.string().nullable(),
    data: InvitationDataSchema,
    slug: z.string().nullable().optional(),
    isPasswordProtected: z.boolean().default(false),
    status: InvitationStatusSchema.default('PUBLISHED'),
    viewCount: z.number().default(0),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    expiresAt: z.string().datetime().optional(),
    // 항목별 활성화 — true면 워터마크 제거 + 영구화(expiresAt=null)
    activated: z.boolean().optional().default(false),
    // 비공개 전환 시각(ISO). null/생략 = 공개. 비공개면 비밀번호를 아는 사람만 열람 (FIKET-459)
    privateAt: z.string().datetime().nullable().optional(),
    // 비밀번호 틀린 누적 횟수(소유자 화면용). INVITATION_PASSWORD_MAX_ATTEMPTS 이상 = 잠김
    passwordFailCount: z.number().int().optional(),
});
// ============================================================
// Create/Update
// ============================================================
/** 슬러그 검증: 3~50자, 소문자 영숫자 + 하이픈, 시작/끝은 영숫자 */
export const SlugSchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/, '슬러그는 3~50자의 소문자 영숫자와 하이픈만 허용됩니다')
    .nullable()
    .optional();
export const CreateInvitationSchema = z.object({
    templateId: z.string().min(1, '템플릿을 선택해주세요'),
    type: InvitationTypeSchema.default('COUPLE').optional(),
    slug: SlugSchema,
    data: InvitationDataSchema.optional(),
});
// status, publishedAt, 비밀번호·비공개 상태는 클라이언트가 이 스키마로 변경할 수 없다.
// — status: 생성 시 'PUBLISHED' 고정, DELETED는 soft-delete DELETE 라우트에서만 전환
// — publishedAt: 생성 시 1회만 기록
// — isPasswordProtected/passwordHash/privateAt: POST /:id/privacy 한 곳에서만 쓴다 (FIKET-459)
export const UpdateInvitationSchema = z.object({
    templateId: z.string().nullable().optional(),
    data: InvitationDataSchema.optional(),
    // slug 형식 검증은 라우트 경계가 아닌 핸들러에서 "변경된 경우에만" 수행한다.
    // (자동 발급된 기존 slug를 자동저장이 그대로 round-trip 할 때 거부되지 않도록)
    slug: z.string().max(50).nullable().optional(),
});
// ============================================================
// 비공개 전환 (FIKET-459)
// ============================================================
export const INVITATION_PASSWORD_MIN = 4;
export const INVITATION_PASSWORD_MAX = 20;
/** POST /api/invitations/:id/privacy 요청. private = 비밀번호 설정·변경 겸용, public = 공개 복귀(비밀번호 해제) */
export const InvitationPrivacySchema = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('private'),
        password: z
            .string()
            .min(INVITATION_PASSWORD_MIN, `비밀번호는 ${INVITATION_PASSWORD_MIN}자 이상이어야 합니다`)
            .max(INVITATION_PASSWORD_MAX, `비밀번호는 ${INVITATION_PASSWORD_MAX}자 이하여야 합니다`),
    }),
    z.object({ mode: z.literal('public') }),
    // 소유자가 틀린 횟수(잠김)를 초기화 — 비밀번호·공개 상태는 그대로
    z.object({ mode: z.literal('reset-attempts') }),
]);
/** 비밀번호 틀린 횟수 — 이 값 이상이면 그 청첩장의 비밀번호 입력이 잠긴다(소유자 초기화·비밀번호 변경까지) */
export const INVITATION_PASSWORD_MAX_ATTEMPTS = 5;
/** 이 횟수부터 하객에게 "N회 틀렸어요" 를 보여준다 */
export const INVITATION_PASSWORD_WARN_ATTEMPTS = 3;
//# sourceMappingURL=invitation.js.map